// The sidebar's sections. Each later phase replaces a "coming" page with the real one; `phase` says when.

import {
  AudioLines,
  BarChart3,
  Bell,
  BookOpen,
  Blocks,
  BookUser,
  Code2,
  GitBranch,
  House,
  Phone,
  PhoneCall,
  Plug,
  ScrollText,
  Settings,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Store,
  Table2,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react'

import type { Permission } from '@/api/types'
import type { Hue } from '@/lib/hues'

export type NavItem = {
  path: string
  label: string
  icon: LucideIcon
  /** The icon's accent hue (src/lib/hues.ts), so each place is easy to spot. */
  hue: Hue
  permission: Permission
  /** What the section holds, in a sentence. */
  summary: string
}

export const NAV_ITEMS: NavItem[] = [
  {
    path: 'home',
    label: 'Home',
    icon: House,
    hue: 'blue',
    permission: 'org.read',
    summary: 'Where your org stands: setup, recent calls and what needs you.',
  },
  {
    path: 'calls',
    label: 'Calls',
    icon: PhoneCall,
    hue: 'green',
    permission: 'calls.read',
    summary: 'Every call with its transcript and recording; test calls now, live calls and transfers next.',
  },
  {
    path: 'contacts',
    label: 'Contacts',
    icon: BookUser,
    hue: 'amber',
    permission: 'contacts.read',
    summary: 'The shoppers you may call, with their consent and opt-outs. Add them one by one or import a CSV.',
  },
  {
    path: 'orders',
    label: 'Orders',
    icon: ShoppingBag,
    hue: 'teal',
    permission: 'contacts.read',
    summary: 'Your stores’ orders, kept in step: search them, see where each stands and what the store sent.',
  },
  {
    path: 'flows',
    label: 'Flows',
    icon: GitBranch,
    hue: 'violet',
    permission: 'flows.read',
    summary:
      'Ready-made ecommerce flows (order verification, checkout help, delivery slots and more) and the builder to make your own, with a browser simulation before any real call.',
  },
  {
    path: 'results',
    label: 'Results',
    icon: Table2,
    hue: 'teal',
    permission: 'results.read',
    summary: 'Every answer from every call as a row, with the recording and transcript behind it. Export to CSV.',
  },
  {
    path: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    hue: 'rose',
    permission: 'results.read',
    summary: 'Answer rates, outcomes over time, cost per outcome and transfer rates, by flow and date.',
  },
  {
    path: 'stores',
    label: 'Stores',
    icon: Store,
    hue: 'green',
    permission: 'org.read',
    summary: 'Your stores’ orders, customers and returns, kept in step: connect Shopify, or send them by API.',
  },
  {
    path: 'integrations',
    label: 'Integrations',
    icon: Blocks,
    hue: 'violet',
    permission: 'org.read',
    summary: 'Apps that work with RowCall, such as Glood: connect one and it starts calls for you.',
  },
  {
    path: 'developers',
    label: 'Developers',
    icon: Code2,
    hue: 'blue',
    permission: 'developers.manage',
    summary: 'API keys, webhooks and the actions your flows can take through your systems.',
  },
]

export const SETTINGS_ITEM = { path: 'settings', label: 'Settings', icon: Settings }

/**
 * Each Settings page's mark: its tab's icon and accent hue, which the page's own sections share (so Billing is green
 * on its tab and its cards). Keyed by the page's path under /settings ('' is General).
 */
export const SETTINGS_MARKS: Record<string, { icon: LucideIcon; hue: Hue }> = {
  '': { icon: SlidersHorizontal, hue: 'blue' },
  voices: { icon: AudioLines, hue: 'violet' },
  sop: { icon: BookOpen, hue: 'amber' },
  'phone-numbers': { icon: Phone, hue: 'green' },
  members: { icon: Users, hue: 'blue' },
  roles: { icon: ShieldCheck, hue: 'teal' },
  'connected-apps': { icon: Plug, hue: 'violet' },
  billing: { icon: Wallet, hue: 'green' },
  alerts: { icon: Bell, hue: 'amber' },
  audit: { icon: ScrollText, hue: 'teal' },
}
